// apps/client/src/hooks/useConversationMessages.ts
//
// Firestore subscription to the messages subcollection of a single
// conversation, ordered by timestamp ascending so the thread renders
// oldest → newest with the latest message at the bottom (canonical
// plan §1.11 ConversationThreadPage spec).
//
// Schema mirrors canonical plan §1.5 MessageDoc. Note that for outbound
// messages the doc-id is either the real `metaMessageId` returned from
// the Send API (post-swap) or a transient `local:<uuid>` placeholder
// (optimistic UI window). useSendOutboundMessage adds optimistic docs
// via append-to-array state; this subscription is the source of truth
// for everything else.
import { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface MessageAttachment {
  type: string;
  url: string | null;
}

export type MessageDirection = 'inbound' | 'outbound';
export type MessageSendStatus =
  | 'pending'
  | 'sent'
  | 'sent_unconfirmed'
  | 'failed';

export interface MessageDoc {
  /** Firestore doc id. mid for inbound; mid OR `local:<uuid>` for outbound. */
  id: string;
  mid: string;
  direction: MessageDirection;
  senderPsid: string | null;
  senderBrokerId: string | null;
  timestamp: Timestamp | null;
  text: string | null;
  attachments: MessageAttachment[];
  sendStatus: MessageSendStatus | null;
  sendError: string | null;
  metaMessageId: string | null;
  receivedAt: Timestamp | null;
}

interface UseConversationMessagesResult {
  messages: MessageDoc[];
  loading: boolean;
  error: string | null;
}

export function useConversationMessages(
  companyId: string | undefined,
  conversationId: string | undefined
): UseConversationMessagesResult {
  const [messages, setMessages] = useState<MessageDoc[]>([]);
  const [loading, setLoading] = useState<boolean>(
    Boolean(companyId && conversationId)
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId || !conversationId) {
      setLoading(false);
      setMessages([]);
      return;
    }
    setLoading(true);
    setError(null);

    const q = query(
      collection(
        db,
        `companies/${companyId}/conversations/${conversationId}/messages`
      ),
      orderBy('timestamp', 'asc')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs: MessageDoc[] = [];
        snap.forEach((d) => {
          const data = d.data() as Partial<MessageDoc>;
          docs.push({
            id: d.id,
            mid: data.mid ?? d.id,
            direction: (data.direction as MessageDirection) ?? 'inbound',
            senderPsid: data.senderPsid ?? null,
            senderBrokerId: data.senderBrokerId ?? null,
            timestamp: data.timestamp ?? null,
            text: data.text ?? null,
            attachments: Array.isArray(data.attachments)
              ? (data.attachments as MessageAttachment[])
              : [],
            sendStatus: (data.sendStatus as MessageSendStatus) ?? null,
            sendError: data.sendError ?? null,
            metaMessageId: data.metaMessageId ?? null,
            receivedAt: data.receivedAt ?? null,
          });
        });
        setMessages(docs);
        setLoading(false);
      },
      (err) => {
        console.error('[useConversationMessages] subscription failed', err);
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    );

    return () => unsub();
  }, [companyId, conversationId]);

  return { messages, loading, error };
}
